import { Anchor } from 'src/design/atoms/Anchor';
import { Grid } from 'src/design/atoms/Grid';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import React from 'react';
import { Link } from 'react-router-dom';

import { JobSummaryDto } from 'src/api/FiretrolApiClient';
import { formatCurrency } from 'src/utils';

import { CTCJobDto, getJobType } from 'src/routes/CTC/ViewCTCJob/helpers';

interface JobInfoCardProps {
  job: JobSummaryDto;
  linkedJob: CTCJobDto | null;
  customerName: string;
  bonded: string;
  showJobLink: boolean;
  onTeamClick: () => void;
}

const LabeledValue: React.FC<{
  label: string;
  value: string | React.ReactNode;
}> = ({ label, value }) => (
  <Text>
    <Text span c="black">
      {label}:
    </Text>{' '}
    {value}
  </Text>
);

export const JobInfoCard: React.FC<JobInfoCardProps> = ({
  job,
  linkedJob,
  customerName,
  bonded,
  showJobLink,
  onTeamClick,
}) => {
  const jobLabel = `${job.wsJobNumber} - ${job.wsJobName}`;
  return (
    <Paper>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <Stack gap="xs">
            <LabeledValue
              label="Job Name"
              value={
                showJobLink && linkedJob ? (
                  <Anchor
                    component={Link}
                    to={`/jobs/${linkedJob.id}/summary`}
                    c="blue"
                  >
                    {jobLabel}
                  </Anchor>
                ) : (
                  jobLabel
                )
              }
            />
            <LabeledValue
              label="Customer"
              value={`${job.custNmbr} - ${customerName}`}
            />
            <LabeledValue label="Job Type" value={getJobType(job.divisions)} />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3, xl: 2 }}>
          <Stack gap="xs">
            <LabeledValue
              label="Project Team"
              value={
                linkedJob && linkedJob?.team?.length > 0 ? (
                  <Anchor onClick={onTeamClick}>
                    {linkedJob?.team.length} Members
                  </Anchor>
                ) : (
                  'N/A'
                )
              }
            />
            <LabeledValue label="Division" value={job.divisions} />
            <LabeledValue label="Salesperson" value={job.salesPersonName} />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3, xl: 2 }}>
          <Stack gap="xs">
            <LabeledValue
              label="Certified Payroll"
              value={job.certifiedPayroll === 1 ? 'Yes' : 'No'}
            />
            <LabeledValue label="Bonded" value={bonded} />
            <LabeledValue label="OCIP" value={job.ocip || 'No'} />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3, xl: 2 }}>
          <Stack gap="xs">
            <LabeledValue label="LDS" value={job.liquidatedDamages || 'No'} />
            <LabeledValue
              label="Tax Exempt"
              value={linkedJob ? (linkedJob.taxExempt ? 'Yes' : 'No') : 'N/A'}
            />
            <LabeledValue
              label="Vendor Discounts"
              value={
                linkedJob ? (linkedJob.vendorDiscounts ? 'Yes' : 'No') : 'N/A'
              }
            />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3, xl: 2 }}>
          <Stack gap="xs">
            <LabeledValue
              label="Per Diem"
              value={
                linkedJob ? (linkedJob.perDiemEnabled ? 'Yes' : 'No') : 'N/A'
              }
            />
            <LabeledValue
              label="Per Diem Amount"
              value={
                linkedJob &&
                linkedJob.perDiemEnabled &&
                linkedJob.perDiemAmount != null
                  ? formatCurrency(Number(linkedJob.perDiemAmount), {
                      precise: true,
                    })
                  : 'N/A'
              }
            />
          </Stack>
        </Grid.Col>
      </Grid>
    </Paper>
  );
};
